import { CheckCircleIcon } from '@heroicons/react/outline'
import StrategyCallButton from './StrategyCallButton'

const COLORS = {
    dark: '#1F2937',
    accent: '#ED5C2F',
    accentLight20: 'color-mix(in oklab, #ed5c2f 20%, transparent)',
    accentLight30: 'color-mix(in oklab, #ed5c2f 30%, transparent)',
    accentLight40: 'color-mix(in oklab, #ed5c2f 40%, transparent)',
    accentLight50: 'color-mix(in oklab, #ed5c2f 50%, transparent)',
    accentLight60: 'color-mix(in oklab, #ed5c2f 60%, transparent)',
    accentLight70: 'color-mix(in oklab, #ed5c2f 70%, transparent)',
    text50: 'color-mix(in oklab, #1f2937 50%, transparent)',
    text60: 'color-mix(in oklab, #1f2937 60%, transparent)',
    text70: 'color-mix(in oklab, #1f2937 70%, transparent)',
    text80: 'color-mix(in oklab, #1f2937 80%, transparent)',
    borderSoft: 'color-mix(in oklab, #1f2937 10%, transparent)',
    sectionBg: 'color-mix(in oklab, #fff7ed 60%, transparent)',
    panelBg: 'color-mix(in oklab, #fff7ed 70%, transparent)',
}

const defaultContent = {
    number: '04',
    eyebrow: 'Response Tracking',
    titlePrefix: 'Know what worked. ',
    titleHighlight: 'Improve every drop.',
    description:
        'Direct mail only gets better when you measure it. We can add call tracking numbers and QR codes to your campaign so you can see which audiences, offers, and creative are actually driving response, and use that signal to sharpen the next drop.',
    includedHeading: "What's included",
    includedItems: [
        'Dedicated call tracking numbers',
        'QR code tracking',
        'Call volume & timing reports',
        'Scan volume & timing reports',
        'Response attribution by campaign',
        'A/B response comparison',
    ],
    cta: {
        label: 'Talk to our team',
        href: '/contact-us',
    },
    tabs: ['7d', '30d', '90d'],
    activeTab: '30d',
    statCards: [
        { label: 'Responses', value: '1,490', change: '18.4%' },
        { label: 'Calls', value: '1,142', change: '22%' },
        { label: 'QR scans', value: '348', change: '9%' },
    ],
    bars: [
        { height: '20%', color: 'accentLight20' },
        { height: '35%', color: 'accentLight30' },
        { height: '45%', color: 'accentLight40' },
        { height: '55%', color: 'accentLight50' },
        { height: '65%', color: 'accentLight60' },
        { height: '75%', color: 'accentLight70' },
        { height: '85%', color: 'accentLight70', hoverColor: '#ed5c2f' },
        { height: '100%', color: 'accent', hoverColor: '#f28230' },
    ],
    sources: [
        {
            label: 'Tracked phone number',
            value: '1,142',
            width: '78%',
        },
        {
            label: 'QR code scan',
            value: '348',
            width: '24%',
        },
    ],
}

function IncludedItem({ children }) {
    return (
        <div className="flex items-start gap-2">
            <CheckCircleIcon
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: COLORS.accent }}
            />
            <div className="text-sm leading-7" style={{ color: COLORS.text80 }}>
                {children}
            </div>
        </div>
    )
}

function StatCard({ label, value, change }) {
    return (
        <div
            className="rounded-xl border p-3"
            style={{
                background: COLORS.panelBg,
                borderColor: COLORS.borderSoft,
            }}>
            <div
                className="text-xs font-semibold uppercase tracking-[1.2px] leading-7"
                style={{ color: COLORS.text50 }}>
                {label}
            </div>
            <div
                className="mt-1 text-2xl font-semibold tabular-nums"
                style={{ color: COLORS.dark }}>
                {value}
            </div>
            <div
                className="mt-1 text-xs font-bold leading-7"
                style={{ color: COLORS.accent }}>
                {`↑ ${change}`}
            </div>
        </div>
    )
}

function ResponseSource({ label, value, width }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="flex flex-1 flex-col gap-1">
                <div
                    className="text-sm font-semibold leading-7"
                    style={{ color: COLORS.dark }}>
                    {label}
                </div>
                <div
                    className="h-1.5 overflow-hidden rounded-full"
                    style={{ backgroundColor: COLORS.borderSoft }}>
                    <div
                        className="h-full rounded-full bg-[#ed5c2f]"
                        style={{ width }}
                    />
                </div>
            </div>
            <div
                className="text-sm font-bold leading-7 tabular-nums"
                style={{ color: COLORS.dark }}>
                {value}
            </div>
        </div>
    )
}

export default function ResponseTrackingSection(props) {
    const number = props?.number || defaultContent.number
    const eyebrow = props?.eyebrow || defaultContent.eyebrow
    const titlePrefix = props?.titlePrefix || defaultContent.titlePrefix
    const titleHighlight =
        props?.titleHighlight || defaultContent.titleHighlight
    const description = props?.description || defaultContent.description
    const includedHeading =
        props?.includedHeading || defaultContent.includedHeading
    const includedItems =
        Array.isArray(props?.includedItems) && props.includedItems.length > 0
            ? props.includedItems
            : defaultContent.includedItems
    const cta = props?.cta || defaultContent.cta
    const tabs =
        Array.isArray(props?.tabs) && props.tabs.length > 0
            ? props.tabs
            : defaultContent.tabs
    const activeTab = props?.activeTab || defaultContent.activeTab
    const statCards =
        Array.isArray(props?.statCards) && props.statCards.length > 0
            ? props.statCards
            : defaultContent.statCards
    const bars =
        Array.isArray(props?.bars) && props.bars.length > 0
            ? props.bars
            : defaultContent.bars
    const sources =
        Array.isArray(props?.sources) && props.sources.length > 0
            ? props.sources
            : defaultContent.sources

    return (
        <section
            id="response-tracking"
            className="border-y scroll-mt-24"
            style={{
                background: COLORS.sectionBg,
                borderColor: COLORS.borderSoft,
            }}>
            <div className="container mx-auto px-4 py-16 lg:py-24">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <div
                        className="flex flex-col gap-4 rounded-3xl border bg-white p-6 shadow-lg transition-shadow duration-300 hover:shadow-xl lg:order-2"
                        style={{ borderColor: COLORS.borderSoft }}>
                        <div
                            className="flex items-center justify-between border-b pb-4"
                            style={{ borderColor: COLORS.borderSoft }}>
                            <div
                                className="text-sm font-bold leading-7"
                                style={{ color: COLORS.dark }}>
                                Campaign Performance
                            </div>

                            <div className="flex gap-1">
                                {tabs.map((tab) => {
                                    const isActive = tab === activeTab
                                    return (
                                        <div
                                            key={tab}
                                            className="rounded px-2.5 py-1"
                                            style={{
                                                backgroundColor: isActive
                                                    ? COLORS.dark
                                                    : COLORS.panelBg,
                                            }}>
                                            <div
                                                className="text-xs font-semibold leading-7"
                                                style={{
                                                    color: isActive
                                                        ? '#ffffff'
                                                        : COLORS.text60,
                                                }}>
                                                {tab}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            {statCards.map((card) => (
                                <StatCard key={card.label} {...card} />
                            ))}
                        </div>

                        <div className="flex h-20 items-end gap-1.5">
                            {bars.map((bar, index) => (
                                <div
                                    key={`${bar.height}-${index}`}
                                    className="flex-1 rounded-t transition-colors duration-200"
                                    style={{
                                        height: bar.height,
                                        backgroundColor:
                                            COLORS[bar.color] || bar.color,
                                    }}
                                />
                            ))}
                        </div>

                        <div className="flex flex-col gap-3 pt-2">
                            <div
                                className="text-xs font-bold uppercase tracking-[1.2px] leading-7"
                                style={{ color: COLORS.text50 }}>
                                Response by source
                            </div>

                            {sources.map((source) => (
                                <ResponseSource
                                    key={source.label}
                                    {...source}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-5 lg:order-1">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ed5c2f]">
                                <div className="text-sm font-bold leading-7 text-white">
                                    {number}
                                </div>
                            </div>
                            <div
                                className="text-xs uppercase tracking-[1.2px]"
                                style={{ color: COLORS.text50 }}>
                                {eyebrow}
                            </div>
                        </div>

                        <h2
                            className="text-3xl font-[700] tracking-tight md:text-[44px] md:leading-[1.1]"
                            style={{ color: COLORS.dark }}>
                            {titlePrefix}
                            <span style={{ color: COLORS.accent }}>
                                {titleHighlight}
                            </span>
                        </h2>

                        <p
                            className="text-lg leading-7"
                            style={{ color: COLORS.text70 }}>
                            {description}
                        </p>

                        <div
                            className="mt-2 rounded-2xl border bg-white p-6"
                            style={{
                                borderColor:
                                    'color-mix(in oklab, #ed5c2f 15%, transparent)',
                            }}>
                            <div
                                className="mb-3 text-xs font-bold uppercase tracking-[1.2px] leading-7"
                                style={{ color: COLORS.accent }}>
                                {includedHeading}
                            </div>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-2">
                                {includedItems.map((item, index) => (
                                    <IncludedItem key={`${item}-${index}`}>
                                        {item}
                                    </IncludedItem>
                                ))}
                            </div>
                        </div>

                        {cta?.label ? (
                            <div>
                                <StrategyCallButton className="inline-flex w-fit rounded-full bg-rtl-gradient px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:opacity-95">
                                    {cta.label}
                                </StrategyCallButton>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    )
}
